import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaCloudUploadAlt,
  FaSearch,
  FaTrash,
  FaUserPlus,
  FaUsers
} from "react-icons/fa";
import {
  createClassroomTransitionDraft,
  discardClassroomTransitionDraft,
  getClassroomTransitionRoster,
  searchClassroomTransitionChildren,
  updateClassroomTransitionDraft
} from "../services/TeacherAPIClient";
import {
  getClassroomTransitionRows,
  matchUploadedChildren,
  normaliseRosterName,
  normaliseTransitionChildren,
  parseClassroomTransitionFile,
  sortClassroomTransitionChildren,
  toClassroomTransitionAllocation
} from "../utils/classroomTransitionRoster";

const normaliseDraft = (response) => {
  const draft = response?.draft || response?.classroomDraft || response;
  if (!draft) return null;
  return {
    ...draft,
    id: draft.id || draft._id || draft.draftId || ""
  };
};

const getExistingChildContext = (child) => normaliseRosterName(
  child?.classDisplayName || [
    child?.className,
    child?.grade,
    child?.academicYear
  ].filter(Boolean).join(" · ")
);

function TeacherClassroomPopulationWorkspace({
  mode,
  routeContext,
  initialChildren = null,
  developmentMock = false,
  onBack,
  onReview
}) {
  const fileInputRef = useRef(null);
  const manualSearchTimerRef = useRef(null);
  const existingSearchRequestRef = useRef(0);
  const [allChildren, setAllChildren] = useState([]);
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [unresolvedRows, setUnresolvedRows] = useState([]);
  const [selectedRemainingIds, setSelectedRemainingIds] = useState(new Set());
  const [selectedRemovalIds, setSelectedRemovalIds] = useState(new Set());
  const [draft, setDraft] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [remainingSearch, setRemainingSearch] = useState("");
  const [selectedSearch, setSelectedSearch] = useState("");
  const [candidateSelections, setCandidateSelections] = useState({});
  const [existingSearch, setExistingSearch] = useState(null);
  const [manualAddOpen, setManualAddOpen] = useState(false);
  const [manualChildName, setManualChildName] = useState("");

  useEffect(() => () => {
    clearTimeout(manualSearchTimerRef.current);
    existingSearchRequestRef.current += 1;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    const rosterRequest = developmentMock && Array.isArray(initialChildren)
      ? Promise.resolve({ availableChildren: initialChildren })
      : getClassroomTransitionRoster(routeContext);

    rosterRequest
      .then((response) => {
        if (cancelled) return;
        const children = normaliseTransitionChildren(getClassroomTransitionRows(response, [
          "availableChildren",
          "remainingChildren",
          "children"
        ]));
        const restoredDraft = normaliseDraft(
          response?.draft || response?.classroomDraft || (response?.allocations ? response : null)
        );
        const restoredAllocations = getClassroomTransitionRows(
          restoredDraft,
          ["allocations", "children", "roster"]
        );
        const restoredChildren = restoredAllocations.map((allocation, index) => {
          const childId = allocation?.childId || allocation?.studentId || allocation?.id;
          const existingChild = children.find((child) => child.id === childId);
          if (existingChild) {
            return {
              ...existingChild,
              source: allocation?.source || restoredDraft?.method
            };
          }
          if (allocation?.kind === "new" || allocation?.newChild) {
            return {
              id: allocation?.id || `new:restored-${index + 1}`,
              name: normaliseRosterName(allocation?.name),
              newChild: true,
              source: allocation?.source || restoredDraft?.method
            };
          }
          return {
            id: childId,
            name: normaliseRosterName(allocation?.name || allocation?.child?.name),
            source: allocation?.source || restoredDraft?.method
          };
        }).filter((child) => child.id && child.name);
        const combinedChildren = normaliseTransitionChildren([
          ...children,
          ...restoredChildren.filter((child) => !child.newChild)
        ]);
        const restoredUnresolved = (Array.isArray(restoredDraft?.unresolvedRows)
          ? restoredDraft.unresolvedRows
          : []).map((row) => ({
            ...row,
            candidates: Array.isArray(row.candidates)
              ? normaliseTransitionChildren(row.candidates)
              : combinedChildren.filter((child) => row.candidateIds?.includes(child.id))
          }));

        setAllChildren(combinedChildren);
        setDraft(restoredDraft);
        setSelectedChildren(sortClassroomTransitionChildren(restoredChildren));
        setUnresolvedRows(restoredUnresolved);
        if (restoredDraft?.sourceFilename && Number(restoredDraft.sourceRowCount) > 0) {
          setUploadedFile({
            name: restoredDraft.sourceFilename,
            rowCount: restoredDraft.sourceRowCount
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Last year’s remaining children could not be loaded. Close and try again.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [developmentMock, initialChildren, routeContext]);

  const selectedIds = useMemo(
    () => new Set(selectedChildren.filter((child) => !child.newChild).map((child) => child.id)),
    [selectedChildren]
  );
  const remainingChildren = useMemo(() => allChildren.filter((child) =>
    !selectedIds.has(child.id) &&
    child.name.toLowerCase().includes(remainingSearch.trim().toLowerCase())
  ), [allChildren, remainingSearch, selectedIds]);
  const allVisibleRemainingSelected = remainingChildren.length > 0 &&
    remainingChildren.every((child) => selectedRemainingIds.has(child.id));
  const visibleSelectedChildren = useMemo(() => selectedChildren.filter((child) =>
    child.name.toLowerCase().includes(selectedSearch.trim().toLowerCase())
  ), [selectedChildren, selectedSearch]);

  const toggleAllVisibleRemaining = () => {
    setSelectedRemainingIds((currentIds) => {
      const nextIds = new Set(currentIds);
      const visibleIds = remainingChildren.map((child) => child.id);
      const allVisibleSelected = visibleIds.every((childId) => currentIds.has(childId));

      visibleIds.forEach((childId) => {
        if (allVisibleSelected) nextIds.delete(childId);
        else nextIds.add(childId);
      });

      return nextIds;
    });
  };

  const saveDraft = async (
    nextChildren,
    nextUnresolved,
    sourceFilename = uploadedFile?.name,
    sourceRowCount = uploadedFile?.rowCount
  ) => {
    const payload = {
      progressionPointId: routeContext.progressionPointId,
      classroomName: routeContext.classroomName,
      teacherIntent: routeContext.teacherIntent,
      method: mode,
      ...(draft?.id ? { version: draft.version } : {}),
      allocations: nextChildren.map((child) => toClassroomTransitionAllocation(child, mode)),
      unresolvedRows: nextUnresolved.map((row) => ({
        id: row.id,
        uploadedName: row.uploadedName,
        matchType: row.matchType,
        candidateIds: row.candidates.map((candidate) => candidate.id)
      })),
      sourceFilename: mode === "upload" ? sourceFilename ?? "" : undefined,
      sourceRowCount: mode === "upload" ? sourceRowCount ?? 0 : undefined
    };

    setSaving(true);
    setError("");
    try {
      const savedResponse = developmentMock
        ? {
            id: draft?.id || "development-classroom-draft",
            version: Number(draft?.version || 0) + 1,
            reservationExpiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString()
          }
        : draft?.id
          ? await updateClassroomTransitionDraft({
            ...routeContext,
            ...payload,
            draftId: draft.id
          })
          : await createClassroomTransitionDraft({ ...routeContext, ...payload });
      const savedDraft = normaliseDraft(savedResponse);
      if (!savedDraft?.id || savedDraft.version === undefined || savedDraft.version === null) {
        throw new Error("Invalid classroom draft response");
      }
      setDraft({ ...draft, ...savedDraft });
      return true;
    } catch (saveError) {
      setError(saveError?.response?.status === 409
        ? "This roster changed in another session. Go back and reopen it to load the remaining children."
        : "This classroom draft could not be saved. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setProcessing(true);
    setError("");
    try {
      const parsedFile = await parseClassroomTransitionFile(file);
      const preservedChildren = selectedChildren.filter((child) => child.source !== "upload");
      const preservedIds = new Set(preservedChildren.map((child) => child.id));
      const result = matchUploadedChildren(
        parsedFile.names,
        allChildren.filter((child) => !preservedIds.has(child.id))
      );
      const nextChildren = sortClassroomTransitionChildren([
        ...preservedChildren,
        ...result.matched
      ]);
      const saved = await saveDraft(
        nextChildren,
        result.unresolved,
        parsedFile.filename,
        parsedFile.names.length
      );
      if (saved) {
        setSelectedChildren(nextChildren);
        setUnresolvedRows(result.unresolved);
        setUploadedFile({ name: parsedFile.filename, rowCount: parsedFile.names.length });
        setCandidateSelections({});
        setExistingSearch(null);
      }
    } catch (parseError) {
      setError(parseError.message);
    } finally {
      setProcessing(false);
    }
  };

  const discardUpload = async () => {
    setSaving(true);
    setError("");
    try {
      const preservedChildren = selectedChildren.filter((child) => child.source !== "upload");
      if (draft?.id && preservedChildren.length) {
        const saved = await saveDraft(preservedChildren, [], null, null);
        if (!saved) return;
      } else if (draft?.id && !developmentMock) {
        await discardClassroomTransitionDraft({
          ...routeContext,
          draftId: draft.id,
          version: draft.version
        });
      }
      if (!preservedChildren.length) setDraft(null);
      setUploadedFile(null);
      setSelectedChildren(preservedChildren);
      setUnresolvedRows([]);
      setCandidateSelections({});
      setExistingSearch(null);
    } catch (discardError) {
      setError(discardError?.response?.status === 409
        ? "This draft changed in another session. Go back and reopen it before discarding."
        : "The uploaded list could not be discarded. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const saveResolvedRow = async (nextChildren, rowId) => {
    const nextUnresolved = unresolvedRows.filter((item) => item.id !== rowId);
    if (await saveDraft(nextChildren, nextUnresolved)) {
      setSelectedChildren(nextChildren);
      setUnresolvedRows(nextUnresolved);
      setExistingSearch(null);
      return true;
    }
    return false;
  };

  const resolveWithChild = async (row, child) => {
    if (!child || selectedIds.has(child.id)) return;
    const nextChildren = sortClassroomTransitionChildren([
      ...selectedChildren,
      { ...child, uploadedName: row.uploadedName, source: "upload" }
    ]);
    await saveResolvedRow(nextChildren, row.id);
  };

  const removeUnresolved = async (rowId) => {
    const nextUnresolved = unresolvedRows.filter((row) => row.id !== rowId);
    if (await saveDraft(selectedChildren, nextUnresolved)) {
      setUnresolvedRows(nextUnresolved);
      setExistingSearch(null);
    }
  };

  const removeSelected = async (child) => {
    const nextChildren = selectedChildren.filter((item) => item.id !== child.id);
    if (await saveDraft(nextChildren, unresolvedRows)) {
      setSelectedChildren(nextChildren);
    }
  };

  const runExistingSearch = async ({ row = null, query }) => {
    const trimmedQuery = normaliseRosterName(query);
    if (trimmedQuery.length < 2) return;
    const requestId = existingSearchRequestRef.current + 1;
    existingSearchRequestRef.current = requestId;
    setExistingSearch({ rowId: row?.id || "manual", query: trimmedQuery, loading: true, results: [] });
    setError("");
    try {
      const response = developmentMock
        ? {
            children: allChildren.filter((child) =>
              child.name.toLowerCase().includes(trimmedQuery.toLowerCase())
            )
          }
        : await searchClassroomTransitionChildren({
            ...routeContext,
            query: trimmedQuery,
            limit: 25
          });
      const results = normaliseTransitionChildren(
        getClassroomTransitionRows(response, ["children", "results", "rows"])
      );
      if (requestId !== existingSearchRequestRef.current) return;
      setExistingSearch({ rowId: row?.id || "manual", query: trimmedQuery, loading: false, results });
    } catch (searchError) {
      if (requestId !== existingSearchRequestRef.current) return;
      setExistingSearch(null);
      setError("Existing children could not be searched. Please try again.");
    }
  };

  const closeManualAdd = () => {
    clearTimeout(manualSearchTimerRef.current);
    existingSearchRequestRef.current += 1;
    setManualAddOpen(false);
    setManualChildName("");
    setExistingSearch(null);
    setError("");
  };

  const updateManualChildName = (value) => {
    const query = normaliseRosterName(value);
    clearTimeout(manualSearchTimerRef.current);
    existingSearchRequestRef.current += 1;
    setManualChildName(value);
    setExistingSearch(null);
    setError("");
    if (query.length < 2) return;
    manualSearchTimerRef.current = setTimeout(() => {
      runExistingSearch({ query });
    }, 300);
  };

  const addNewUploadedChild = async (row) => {
    if (selectedChildren.some((child) =>
      normaliseRosterName(child.name).toLowerCase() ===
      normaliseRosterName(row.uploadedName).toLowerCase()
    )) {
      setError(`${row.uploadedName} is already included in this classroom draft.`);
      return;
    }
    const child = {
      id: `new:${row.id}`,
      name: row.uploadedName,
      newChild: true,
      source: "upload"
    };
    const nextChildren = sortClassroomTransitionChildren([...selectedChildren, child]);
    await saveResolvedRow(nextChildren, row.id);
  };

  const addSelectedRemaining = async () => {
    const additions = allChildren.filter((child) => selectedRemainingIds.has(child.id));
    const nextChildren = sortClassroomTransitionChildren([...selectedChildren, ...additions]);
    if (await saveDraft(nextChildren, unresolvedRows)) {
      setSelectedChildren(nextChildren);
      setSelectedRemainingIds(new Set());
    }
  };

  const removeMarkedChildren = async () => {
    const nextChildren = selectedChildren.filter((child) => !selectedRemovalIds.has(child.id));
    if (await saveDraft(nextChildren, unresolvedRows)) {
      setSelectedChildren(nextChildren);
      setSelectedRemovalIds(new Set());
    }
  };

  const addManualExistingChild = async (child) => {
    if (selectedIds.has(child.id)) return;
    const nextChildren = sortClassroomTransitionChildren([...selectedChildren, child]);
    if (await saveDraft(nextChildren, unresolvedRows)) {
      setSelectedChildren(nextChildren);
      closeManualAdd();
    }
  };

  const addManualNewChild = async () => {
    if (selectedChildren.some((child) =>
      normaliseRosterName(child.name).toLowerCase() ===
      normaliseRosterName(manualChildName).toLowerCase()
    )) {
      setError(`${normaliseRosterName(manualChildName)} is already included in this classroom draft.`);
      return;
    }
    const child = {
      id: `new:manual-${Date.now()}`,
      name: normaliseRosterName(manualChildName),
      newChild: true,
      source: "manual"
    };
    const nextChildren = sortClassroomTransitionChildren([...selectedChildren, child]);
    if (await saveDraft(nextChildren, unresolvedRows)) {
      setSelectedChildren(nextChildren);
      closeManualAdd();
    }
  };

  const reviewRoster = () => {
    if (!draft?.id || unresolvedRows.length || !selectedChildren.length || saving) return;
    onReview?.({
      ...routeContext,
      method: mode,
      draftId: draft.id,
      version: draft.version,
      reservationExpiresAt: draft.reservationExpiresAt,
      sourceFilename: uploadedFile?.name || draft.sourceFilename,
      sourceRowCount: uploadedFile?.rowCount || draft.sourceRowCount,
      children: selectedChildren.map((child) => ({
        id: child.newChild ? undefined : child.id,
        name: child.name,
        kind: child.newChild ? "new" : "existing",
        source: child.source || mode
      }))
    });
  };

  if (loading) {
    return (
      <div className="classroom-transition-loading" role="status">
        <Spinner animation="border" size="sm" />
        <span>Loading last year’s remaining children…</span>
      </div>
    );
  }

  const renderExistingSearch = (row = null) => {
    const isCurrentSearch = existingSearch?.rowId === (row?.id || "manual");
    if (!isCurrentSearch) return null;

    return (
      <div className="classroom-population-existing-results" role="region" aria-label="Existing child search results">
        {existingSearch.loading ? (
          <span role="status">Searching existing children…</span>
        ) : existingSearch.results.length ? (
          existingSearch.results.map((child) => {
            const childContext = getExistingChildContext(child);
            return (
              <button
                type="button"
                key={child.id}
                onClick={() => row ? resolveWithChild(row, child) : addManualExistingChild(child)}
                disabled={selectedIds.has(child.id) || saving}
              >
                <span>Use existing child {child.name}</span>
                {childContext && <small>{childContext}</small>}
              </button>
            );
          })
        ) : row ? (
          <button
            type="button"
            className="classroom-population-confirm-new-child"
            onClick={() => addNewUploadedChild(row)}
            disabled={saving}
          >
            Confirm add {row.uploadedName} as a new child
          </button>
        ) : (
          <button
            type="button"
            className="classroom-population-confirm-new-child"
            onClick={addManualNewChild}
            disabled={!normaliseRosterName(manualChildName) || saving}
          >
            Confirm add {normaliseRosterName(manualChildName)} as a new child
          </button>
        )}
      </div>
    );
  };

  const remainingPanel = (
    <section className="classroom-population-panel remaining" aria-labelledby="remaining-children-heading">
      <div className="classroom-population-panel-heading">
        <FaUsers aria-hidden="true" />
        <div>
          <h3 id="remaining-children-heading">
            {mode === "upload" ? "Last year’s remaining children" : "Select from last year’s children"}
          </h3>
          <span>{remainingChildren.length} remaining</span>
        </div>
      </div>
      <label className="classroom-population-search">
        <span className="visually-hidden">Search remaining children</span>
        <FaSearch aria-hidden="true" />
        <input
          type="search"
          placeholder="Search remaining children"
          value={remainingSearch}
          onChange={(event) => setRemainingSearch(event.target.value)}
        />
      </label>
      {mode === "manual" && remainingChildren.length > 0 && (
        <div className="classroom-population-bulk-actions">
          <button
            type="button"
            onClick={toggleAllVisibleRemaining}
            disabled={saving}
          >
            {allVisibleRemainingSelected ? "Deselect all" : "Select all"}
          </button>
        </div>
      )}
      <div className="classroom-population-list" role="list" aria-label="Remaining children">
        {remainingChildren.map((child) => (
          <label className="classroom-population-list-row" role="listitem" key={child.id}>
            {mode === "manual" && (
              <input
                type="checkbox"
                aria-label={`Select ${child.name}`}
                checked={selectedRemainingIds.has(child.id)}
                onChange={(event) => {
                  const nextIds = new Set(selectedRemainingIds);
                  if (event.target.checked) nextIds.add(child.id);
                  else nextIds.delete(child.id);
                  setSelectedRemainingIds(nextIds);
                }}
              />
            )}
            <span>{child.name}</span>
          </label>
        ))}
      </div>
    </section>
  );

  const selectedList = (
    <section className="classroom-population-selected" aria-labelledby="selected-children-heading">
      <div className="classroom-population-selected-heading">
        <FaCheck aria-hidden="true" />
        <h3 id="selected-children-heading">
          {mode === "upload" ? "Matched children" : `Children selected for ${routeContext.classroomName}`} — {selectedChildren.length}
        </h3>
      </div>
      <label className="classroom-population-search">
        <span className="visually-hidden">Search selected children</span>
        <FaSearch aria-hidden="true" />
        <input
          type="search"
          placeholder={mode === "upload" ? "Search matched children" : "Search selected children"}
          value={selectedSearch}
          onChange={(event) => setSelectedSearch(event.target.value)}
        />
      </label>
      <div className="classroom-population-list" role="list" aria-label="Selected children">
        {visibleSelectedChildren.map((child) => (
          <div className="classroom-population-list-row" role="listitem" key={child.id}>
            {mode === "manual" && (
              <input
                type="checkbox"
                aria-label={`Select ${child.name} for removal`}
                checked={selectedRemovalIds.has(child.id)}
                onChange={(event) => {
                  const nextIds = new Set(selectedRemovalIds);
                  if (event.target.checked) nextIds.add(child.id);
                  else nextIds.delete(child.id);
                  setSelectedRemovalIds(nextIds);
                }}
              />
            )}
            <span>{child.name}{child.newChild ? " (new)" : ""}</span>
            <button
              type="button"
              aria-label={`Remove ${child.name} from this classroom`}
              title={`Remove ${child.name}`}
              onClick={() => removeSelected(child)}
              disabled={saving}
            >
              <FaTrash aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );

  const uploadWorkspace = uploadedFile && (
    <div className="classroom-population-workspace upload">
      {remainingPanel}
      <section className="classroom-population-main" aria-label="Uploaded classroom list">
        <div className="classroom-population-upload-heading">
          <strong>{routeContext.classroomName}</strong>
          <span>Uploaded from {uploadedFile.name}</span>
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={processing || saving}>
            Replace file
          </button>
          <button type="button" className="danger" onClick={discardUpload} disabled={saving}>
            <FaTrash aria-hidden="true" /> Discard uploaded list
          </button>
        </div>
        <div className="classroom-population-information">
          Matching the {uploadedFile.rowCount || (
            selectedChildren.filter((child) => child.source === "upload").length + unresolvedRows.length
          )} children from your uploaded list with last year’s children.
        </div>
        {unresolvedRows.length > 0 && (
          <section className="classroom-population-unresolved" aria-labelledby="unresolved-heading">
            <h3 id="unresolved-heading">Needs your help — {unresolvedRows.length}</h3>
            {unresolvedRows.map((row) => {
              const selectedCandidateId = candidateSelections[row.id] || row.candidates[0]?.id || "";
              const selectedCandidate = row.candidates.find((candidate) => candidate.id === selectedCandidateId);
              const searchedThisRow = existingSearch?.rowId === row.id && !existingSearch.loading;
              return (
                <div className="classroom-population-unresolved-row" key={row.id}>
                  <strong>{row.uploadedName}</strong>
                  {row.candidates.length > 0 ? (
                    <>
                      <label>
                        <span className="visually-hidden">Possible matches for {row.uploadedName}</span>
                        <select
                          aria-label={`Possible matches for ${row.uploadedName}`}
                          value={selectedCandidateId}
                          onChange={(event) => setCandidateSelections({
                            ...candidateSelections,
                            [row.id]: event.target.value
                          })}
                        >
                          {row.candidates.map((candidate) => (
                            <option value={candidate.id} key={candidate.id}>{candidate.name}</option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        onClick={() => resolveWithChild(row, selectedCandidate)}
                        disabled={!selectedCandidate || saving}
                      >
                        Match
                      </button>
                    </>
                  ) : (
                    <span>No matches found</span>
                  )}
                  <button
                    type="button"
                    aria-label={`Search existing child for ${row.uploadedName}`}
                    title="Search existing child"
                    onClick={() => runExistingSearch({ row, query: row.uploadedName })}
                    disabled={saving}
                  >
                    <FaSearch aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => runExistingSearch({ row, query: row.uploadedName })}
                    disabled={saving || (searchedThisRow && existingSearch.results.length > 0)}
                  >
                    <FaUserPlus aria-hidden="true" /> Add as new child
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove uploaded row ${row.uploadedName}`}
                    title={`Remove ${row.uploadedName}`}
                    onClick={() => removeUnresolved(row.id)}
                    disabled={saving}
                  >
                    <FaTrash aria-hidden="true" />
                  </button>
                  {renderExistingSearch(row)}
                </div>
              );
            })}
          </section>
        )}
        {selectedList}
      </section>
    </div>
  );

  const manualWorkspace = (
    <>
      <div className="classroom-population-context" aria-label="Classroom and teacher">
        <span><small>Classroom</small><strong>{routeContext.classroomName}</strong></span>
        <span><small>Teacher</small><strong>{routeContext.teacherIntent.name}</strong></span>
      </div>
      <div className="classroom-population-workspace manual">
        {remainingPanel}
        <div className="classroom-population-transfer-actions">
          <button
            type="button"
            className="classroom-population-add-action"
            onClick={addSelectedRemaining}
            disabled={!selectedRemainingIds.size || saving}
          >
            <FaArrowRight aria-hidden="true" /> Add
          </button>
          <button
            type="button"
            className="classroom-population-remove-action"
            onClick={removeMarkedChildren}
            disabled={!selectedRemovalIds.size || saving}
          >
            <FaArrowLeft aria-hidden="true" /> Remove
          </button>
        </div>
        <div className="classroom-population-manual-selected">
          {selectedList}
          {!manualAddOpen && (
            <div className="classroom-population-extra-actions">
              <button
                type="button"
                className="classroom-population-add-child-action"
                onClick={() => {
                  setError("");
                  setManualAddOpen(true);
                }}
              >
                <FaUserPlus aria-hidden="true" /> Add new child
              </button>
            </div>
          )}
          {manualAddOpen && (
            <section
              className="classroom-population-manual-add"
              aria-labelledby="add-child-heading"
              onKeyDown={(event) => {
                if (event.key === "Escape" && !saving) closeManualAdd();
              }}
            >
              <h3 id="add-child-heading">Add new child</h3>
              <label>
                Child name
                <input
                  autoFocus
                  type="text"
                  value={manualChildName}
                  onChange={(event) => updateManualChildName(event.target.value)}
                />
              </label>
              {normaliseRosterName(manualChildName).length < 2 && (
                <p className="classroom-population-manual-add-help">
                  Enter at least 2 characters to search.
                </p>
              )}
              {error && <Alert variant="danger">{error}</Alert>}
              {renderExistingSearch()}
              <Button variant="outline-primary" onClick={closeManualAdd} disabled={saving}>
                Cancel
              </Button>
            </section>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="classroom-population">
      {error && !manualAddOpen && <Alert variant="danger">{error}</Alert>}
      <input
        ref={fileInputRef}
        className="visually-hidden"
        type="file"
        accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        aria-label="Choose classroom list file"
        onChange={handleFile}
      />
      {mode === "upload" && !uploadedFile && (
        <section className="classroom-population-upload-prompt" aria-labelledby="upload-class-list-heading">
          <FaCloudUploadAlt aria-hidden="true" />
          <h3 id="upload-class-list-heading">Upload the children for {routeContext.classroomName}</h3>
          <p>Choose a CSV or XLSX file. The first worksheet is used, up to 500 children and 1 MB.</p>
          <Button onClick={() => fileInputRef.current?.click()} disabled={processing || saving}>
            {processing ? "Matching…" : "Choose file"}
          </Button>
        </section>
      )}
      {mode === "upload" ? uploadWorkspace : manualWorkspace}
      <div className="classroom-population-footer">
        <Button variant="outline-primary" onClick={onBack} disabled={saving || processing}>
          <FaArrowLeft aria-hidden="true" /> Back to previous
        </Button>
        {(mode === "manual" || uploadedFile) && (
          <Button
            variant="primary"
            className="classroom-population-review"
            onClick={reviewRoster}
            disabled={!draft?.id || !selectedChildren.length || unresolvedRows.length > 0 || saving}
          >
            Review {selectedChildren.length} children in this classroom
            {unresolvedRows.length > 0 && <small>Resolve all {unresolvedRows.length} to continue</small>}
          </Button>
        )}
      </div>
    </div>
  );
}

export default TeacherClassroomPopulationWorkspace;
